import {
  Table,
  NotFound,
  Typography,
  Pagination,
  Select,
  Input,
  DSInputType,
  HeadItemType
} from 'components'
import { useEffect, useRef, useState } from 'react'
import { useParams } from 'react-router-dom'

import { Faturamento } from 'services/manage_ov/interface'
import { Skeleton } from '@mui/material'
import { formatToPTBRNumber, numberToDecimalString } from 'commons/utils/number'

import * as S from './styles'

import InvoiceDetailModal from '../InvoiceDetailModal'

import useDebounce from 'commons/customHooks/useDebounce'
import { useDispatch } from 'react-redux'
import { asyncGetInvoicesListByOvNumber } from 'store/OVContext/ovSlice'
import { useSelector } from 'store/store'
import { formatDate } from 'commons/utils/date'

const InvoicesTable = () => {
  const { ovNumber } = useParams()
  const pageSize = 10

  const [searchValue, setSearchValue] = useState('')
  const [currentPage, setCurrentPage] = useState(1)
  const [totalPages, setTotalPages] = useState(0)
  const searchRef = useRef<HTMLInputElement>(null)

  const [invoices, setInvoices] = useState<Faturamento[]>([])

  const [selectedInvoice, setSelectedInvoice] = useState<string>('')
  const [openInvoiceDetail, setOpenInvoiceDetail] = useState(false)
  const [viewOptions, setViewOptions] = useState<'doc' | 'sku'>('doc')

  const dispatch = useDispatch()
  const { invoicesInfo, invoiceIsLoading: loading } = useSelector((state) => state.ov)
  const tableViewOptions = [
    { value: 'doc', title: 'Documento' },
    { value: 'sku', title: 'SKU' }
  ]

  const headValues: HeadItemType[] = [
    { label: 'Doc. Faturamento', columnName: `DocumentoFaturamento` },
    { label: 'Nota Fiscal', columnName: 'NotaFiscal' },
    { label: 'Data Faturamento', columnName: 'DataFaturamento' },
    { label: 'Vlr. Faturado', columnName: 'VlrFaturado' },
    { label: 'Estorno', columnName: 'Estorno' },
    { label: 'Data Estorno', columnName: 'DataEstorno' },
    { label: 'Transportadora 1ª Etapa', columnName: 'Transportadora1Etapa' },
    { label: 'Dt. Prev. Entrg. 1ª Etp.', columnName: 'DtPrevEntrega1Etp' },
    { label: 'Dt. Real Entrg. 1ª Etp.', columnName: 'DtRealEntrega1Etp' },
    { label: 'Transportadora 2ª etapa', columnName: 'Transportadora2Etapa' },
    { label: 'Dt. Prev. Entrg. 2ª Etp.', columnName: 'DtPrevEntrega2Etp' },
    { label: 'Dt. Real Entrg. 2ª Etp.', columnName: 'DtRealEntrega2Etp' }
  ]

  const headSkuValues: HeadItemType[] = [
    { label: 'Material', columnName: 'Material' },
    { label: 'Cor', columnName: 'Cor' },
    { label: 'Tamanho', columnName: 'Tamanho' },
    { label: 'Quantidade', columnName: 'Quantidade' },
    { label: 'Doc. Faturamento', columnName: 'DocumentoFaturamento' },
    { label: 'Nota Fiscal', columnName: 'NotaFiscal' },
    { label: 'Data Faturamento', columnName: 'DataFaturamento' },
    { label: 'Vlr. Faturado', columnName: 'VlrFaturado' },
    { label: 'Estorno', columnName: 'Estorno' },
    { label: 'Data Estorno', columnName: 'DataEstorno' },
    { label: 'Transportadora 1ª Etapa', columnName: 'Transportadora1Etapa' },
    { label: 'Dt. Prev. Entrg. 1ª Etp.', columnName: 'DtPrevEntrega1Etp' },
    { label: 'Dt. Real Entrg. 1ª Etp.', columnName: 'DtRealEntrega1Etp' },
    { label: 'Transportadora 2ª etapa', columnName: 'Transportadora2Etapa' },
    { label: 'Dt. Prev. Entrg. 2ª Etp.', columnName: 'DtPrevEntrega2Etp' },
    { label: 'Dt. Real Entrg. 2ª Etp.', columnName: 'DtRealEntrega2Etp' }
  ]

  const getInvoices = (page?: number, search?: string) => {
    const pageQuery = page || currentPage

    dispatch(
      asyncGetInvoicesListByOvNumber(viewOptions, `${ovNumber}`, {
        currentPage: pageQuery - 1,
        pageSize,
        searchValue: search || ''
      })
    )
    setCurrentPage(pageQuery)
  }
  const debounceChange = useDebounce(getInvoices, 1000)
  useEffect(() => {
    if (invoicesInfo?.Faturamentos?.length > 0) {
      setInvoices(invoicesInfo.Faturamentos)
      const qtdOfRows = invoicesInfo.RowsCount
      const amountOfPages = Math.ceil(qtdOfRows / pageSize)
      setTotalPages(amountOfPages)
      setCurrentPage((currentPg) =>
        currentPg > amountOfPages ? amountOfPages : currentPg
      )
    } else {
      setTotalPages(0)
      setCurrentPage(1)
      setInvoices([])
    }
  }, [invoicesInfo])

  useEffect(() => {
    if (!loading) {
      searchRef.current?.focus()
    }
  }, [loading])

  useEffect(() => {
    getInvoices(1, '')
  }, [ovNumber, viewOptions])

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.toLocaleUpperCase()
    setSearchValue(value)
    debounceChange(1, value)
  }

  const handleInvoiceSelect = (id: string) => {
    setSelectedInvoice(id)
    setOpenInvoiceDetail(true)
  }

  const renderSkeleton = () => {
    const skeleton = []
    for (let i = 0; i < 3; i++) {
      skeleton.push({
        id: `invoice-loading-${i}`,
        content: Array(viewOptions === 'doc' ? headValues.length : headSkuValues.length)
          .fill('')
          .map((_, index) => <Skeleton key={index} width={100} height={20} />)
      })
    }
    return skeleton
  }

  const renderSummary = () => {
    return (
      <S.SummaryWrapper>
        <Pagination
          currentPage={currentPage}
          setCurrentPage={(value: number) => getInvoices(value, searchValue)}
          numberOfPages={totalPages}
          numberOfItems={invoicesInfo.RowsCount}
          itemsPerPage={pageSize}
        />
        <S.SummaryRow>
          <S.SummaryItem>
            <Typography design="hav-default-02-text">Qtd. total</Typography>
            <span>{numberToDecimalString(invoicesInfo?.TotalQuantidade ?? 0)}</span>
          </S.SummaryItem>
          <S.SummaryItem>
            <Typography design="hav-default-02-text">Valor total</Typography>
            <span>{formatToPTBRNumber(invoicesInfo?.TotalValor ?? 0)}</span>
          </S.SummaryItem>
        </S.SummaryRow>
      </S.SummaryWrapper>
    )
  }

  const renderBody = () => {
    const content = loading
      ? renderSkeleton()
      : invoices.map((invoice, index) => {
          return {
            id: `invoice-${index}`,
            onClick: () => handleInvoiceSelect(invoice.NumeroDocumentoFatura),
            content: [
              invoice.NumeroDocumentoFatura,
              `${invoice.NumerNotaFiscal}/${invoice.NumeroSerie}`,
              formatDate(invoice.DataFaturamento),
              formatToPTBRNumber(invoice.ValorTotal),
              invoice.FlagEstorno ? 'Sim' : 'Não',
              invoice.DataEstorno ? formatDate(invoice.DataEstorno) : '',
              invoice.TrasportadoraEtapaUm,
              invoice.DataPrevisaoEntregaUm
                ? formatDate(invoice.DataPrevisaoEntregaUm)
                : '',
              invoice.DataEntregaRealizadaUm
                ? formatDate(invoice.DataEntregaRealizadaUm)
                : '',
              invoice.RazaoSocialTransportadoraDois,
              invoice.DataPrevisaoEntregaDois
                ? formatDate(invoice.DataPrevisaoEntregaDois)
                : '',
              invoice.DataEntregaRealizadaDois
                ? formatDate(invoice.DataEntregaRealizadaDois)
                : ''
            ]
          }
        })
    return content
  }
  const renderSkuBody = () => {
    const content = loading
      ? renderSkeleton()
      : invoices.map((invoice, index) => {
          return {
            id: `invoice-${index}`,
            content: [
              invoice?.CodigoMaterial,
              invoice?.CodigoCor,
              invoice?.CodigoTamnho,
              invoice?.Quantidade,
              invoice.NumeroDocumentoFatura,
              invoice.NumerNotaFiscal,
              formatDate(invoice.DataFaturamento),
              formatToPTBRNumber(invoice.Valor ?? 0),
              invoice.FlagEstorno ? 'Sim' : 'Não',
              invoice.DataEstorno ? formatDate(invoice.DataEstorno) : '',
              invoice.TrasportadoraEtapaUm,
              invoice.DataPrevisaoEntregaUm
                ? formatDate(invoice.DataPrevisaoEntregaUm)
                : '',
              invoice.DataEntregaRealizadaUm
                ? formatDate(invoice.DataEntregaRealizadaUm)
                : '',
              invoice.RazaoSocialTransportadoraDois,
              invoice.DataPrevisaoEntregaDois
                ? formatDate(invoice.DataPrevisaoEntregaDois)
                : '',
              invoice.DataEntregaRealizadaDois
                ? formatDate(invoice.DataEntregaRealizadaDois)
                : ''
            ]
          }
        })

    return content
  }

  return (
    <>
      <S.Header>
        <S.HeaderField width={viewOptions === 'doc' ? '132px' : '83px'}>
          <Select
            name="TableView"
            value={viewOptions}
            options={tableViewOptions}
            onChange={(value) => {
              setViewOptions(value.target.value as unknown as 'doc' | 'sku')
              setSearchValue('')
            }}
            isMenu
          />
        </S.HeaderField>
        <S.HeaderField width="300px">
          <Input
            addorment
            design={DSInputType.SEARCH_INPUT}
            value={searchValue}
            onChange={handleSearch}
            InputProps={{ placeholder: 'Buscar material em faturamento' }}
            disabled={loading}
            ref={searchRef}
          />
        </S.HeaderField>
      </S.Header>
      {!loading && !invoices.length ? (
        <NotFound
          title="Nenhuma fatura encontrada"
          description="As informações de faturamento da Ordem de Venda serão listadas aqui."
        />
      ) : (
        <>
          {viewOptions === 'doc' ? (
            <>
              <S.TableWrapper>
                <Table
                  fixedFirstCol
                  rowHeight={32}
                  headData={headValues}
                  bodyData={renderBody()}
                  distributeWidth={false}
                />
              </S.TableWrapper>
              {renderSummary()}
            </>
          ) : (
            <>
              <S.TableWrapper>
                <Table
                  fixedFirstCol
                  rowHeight={32}
                  headData={headSkuValues}
                  bodyData={renderSkuBody()}
                  distributeWidth={false}
                />
              </S.TableWrapper>
              {renderSummary()}
            </>
          )}
        </>
      )}
      {openInvoiceDetail && (
        <InvoiceDetailModal
          id={selectedInvoice}
          open={openInvoiceDetail}
          setOpen={setOpenInvoiceDetail}
        />
      )}
    </>
  )
}

export default InvoicesTable
